/**
 * Shared types for the brand detail page sections.
 *
 * Sourced from the server fetch in `app/brands/[slug]/page.tsx`. The
 * orchestrator (`brand-client.tsx`) destructures these into the section
 * components so each section's prop contract stays narrow + explicit.
 */

import { badgesForSignals } from "@/components/badges/from-product"
import type { Badge as BadgeData } from "@/components/badges/select"

export interface BrandData {
  id: number
  name: string
  slug: string
  description: string | null
  tier: string | null
  headquarters: string | null
  founding_year: number | null
  parent_company: string | null
  logo_path: string | null
  website_url: string | null
  is_partner: boolean
}

export interface BrandProduct {
  id: string
  title: string
  brand: string
  model: string
  /** FRIDGE-1150 — the minted filter / part numbers; null on appliances. */
  ffn?: string | null
  fpn?: string | null
  seo_slug: string
  images: string[]
  canonical_url: string
  lowest_price_cents: number
  average_price_30d_cents: number | null
  type: string
  capacity_cuft: number | null
  category: string | null
  tags: string[] | null
  specs_json: Record<string, unknown> | null
}

/**
 * The spec keys the brand page's CLIENT reads off a product row — exactly the
 * three `brandProductBadges` consults. Everything else in `specs_json` is
 * server-side data (stats, schema) that must not cross to the client.
 */
export const BRAND_CLIENT_SPEC_KEYS = ['rating', 'reviews', 'energy_star'] as const

/**
 * The most images a brand card ever renders: `ProductGridCard` shows
 * `images.slice(0, 4)` and `alternateCardImage` reads the first image that
 * differs from the lead. Rows arrive from the database with the full gallery.
 */
export const BRAND_CLIENT_MAX_IMAGES = 4

/**
 * Project a database row to what `BrandClient` and its section components
 * actually read (FRIDGE-929, measured 09/17/2026).
 *
 * `products`, `familyProducts` and `carouselProducts` are client props, so
 * Next serialises every row into the React Server Components flight payload
 * of the page. On production /brands/lg that was 51 rows at ~1.1 KB each with
 * the whole `specs_json` (201 B), `tags` (150 B) and full image gallery in
 * every one — and the client reads none of `specs_json` beyond the three
 * badge signals, never reads `tags` or `category`, and renders at most four
 * images. The server keeps the full rows for BrandSchemas and the stats; only
 * this projection crosses to the client. The card output is unchanged by
 * construction: `brandProductBadges`, `productPagePath` and
 * `alternateCardImage` receive the same values they did before (pinned by
 * tests/brand-client-product-projection.test.ts).
 */
export function toBrandClientProduct(p: BrandProduct): BrandProduct {
  const specs = (p.specs_json ?? null) as Record<string, unknown> | null
  let specs_json: Record<string, unknown> | null = null
  if (specs) {
    for (const key of BRAND_CLIENT_SPEC_KEYS) {
      if (specs[key] !== undefined) {
        specs_json = specs_json ?? {}
        specs_json[key] = specs[key]
      }
    }
  }
  return {
    id: p.id,
    title: p.title,
    brand: p.brand,
    model: p.model,
    // The (FFN) of a filter row and the (FPN) of a part row: the grid draws
    // them as the card's number chip and the link names use them to tell
    // same-titled rows apart (FRIDGE-1150). Carried only when minted, so an
    // appliance row adds no bytes to the page payload.
    ...(p.ffn ? { ffn: p.ffn } : {}),
    ...(p.fpn ? { fpn: p.fpn } : {}),
    seo_slug: p.seo_slug,
    images: Array.isArray(p.images) ? p.images.slice(0, BRAND_CLIENT_MAX_IMAGES) : p.images,
    canonical_url: p.canonical_url,
    lowest_price_cents: p.lowest_price_cents,
    average_price_30d_cents: p.average_price_30d_cents,
    type: p.type,
    capacity_cuft: p.capacity_cuft,
    category: null,
    tags: null,
    specs_json,
  }
}

/**
 * One curated CPSC notice for this brand (FRIDGE-401 Step 1 — the recall
 * record is the differentiator a regional dealer's `-overview` page cannot
 * match). Sourced from `cpsc_recalls` by EXACT `primary_brand` equality with
 * `brands.name` — never a substring match (the %GE%→EdgeStar lesson).
 */
export interface BrandRecall {
  recall_number: string
  slug: string
  recall_date: string
  title: string
  hazard_text: string | null
  units_affected_count: number | null
  product_category_label: string | null
}

export interface BrandArticle {
  excerpt?: string
  id: number
  title: string
  slug: string
  image: string | null
  published_at: string
  author_name?: string | null
  /** FRIDGE-1086 — resolved on the server from the authors table; the card shows it. */
  authorImageUrl?: string | null
}

export interface BrandStats {
  productCount: number
  totalProducts: number
  minPrice: number | null
  maxPrice: number | null
  avgPrice: number | null
  /** Median of lowest_price_cents across the FULL live catalog, dollars.
   *  FRIDGE-401 Step 1 — all figures here are full-catalog, never the
   *  20-row carousel sample (see brand-page-render.tsx). */
  medianPrice: number | null
  types: string[]
  fridgeCount: number
  freezerCount: number
  specialtyCount: number
  energyStarCount: number
  /** Capacity span across the live catalog (cu ft), rendered to 2
   *  decimals; null when no row carries a positive capacity_cuft. */
  capacityMinCuft: number | null
  capacityMaxCuft: number | null
}

/**
 * Live retailer-offer aggregate for the brand (FRIDGE-401 Step 1) — the
 * cross-retailer price data a regional dealer's single-store `-overview`
 * page cannot match. Real-or-Hidden: `offerCount === 0` renders nothing.
 */
export interface BrandOfferSummary {
  offerCount: number
  retailerCount: number
  /** Median in-stock offer price in dollars. */
  medianOfferPrice: number | null
}

export interface BrandCollection {
  slug: string
  title: string
}

export interface RelatedBrand {
  name: string
  slug: string
  logo_path: string | null
  tier: string | null
}

/** Type-filter chip on the catalog. `all` is the sentinel for "no filter
 *  applied" — appears as a single visible chip / clear-selection action,
 *  not a concrete product type stored on a row. */
export type CatalogType = "all" | "french-door" | "side-by-side" | "top-freezer" | "bottom-freezer" | "compact" | "water-filter"

/** Concrete (non-sentinel) catalog type keys. The catalog filter accepts a
 *  Set of these so the user can multi-select; an empty set is equivalent
 *  to "All" (no filter). */
export type ConcreteCatalogType = Exclude<CatalogType, "all">

/** Set of catalog types currently active. Empty = no filter (= "All"). */
export type ActiveCatalogTypes = ReadonlySet<ConcreteCatalogType>

/** Buckets shown as filter pills with counts (`All (206)`, `French Door (24)`, …). */
export interface TypeBucket {
  key: CatalogType
  label: string
  count: number
}

/** Pre-baked default tool cards rendered in the "Useful Tools" row. */
export const BRAND_TOOL_CARDS = [
  {
    href: "/tools/energy-cost-calculator",
    title: "Energy Cost Calculator",
    sub: "Estimate annual operating cost based on local rates.",
    glyph: "⚡",
  },
  {
    href: "/tools/refrigerator-size-calculator",
    title: "Refrigerator Size Calculator",
    sub: "Find the right cubic-foot capacity for your household.",
    glyph: "📐",
  },
  {
    href: "/tools/freezer-storage-capacity-calculator",
    title: "Freezer Size Calculator",
    sub: "Estimate freezer capacity by household + storage habits.",
    glyph: "❄️",
  },
] as const

/** Tier palette — re-used from /brands directory redesign. */
export function getTierPillClasses(tier: string | null): { bg: string; fg: string; border?: string } | null {
  if (!tier) return null
  const t = tier.toLowerCase().trim()
  if (t === "popular" || t === "mainstream") return { bg: "#ff9951", fg: "#ffffff" }
  if (t === "luxury") return { bg: "#111827", fg: "#ffffff" }
  if (t === "value") return { bg: "rgba(4,105,240,0.10)", fg: "#0469f0" }
  if (t === "budget") return { bg: "rgba(255,153,81,0.12)", fg: "#c2410c" }
  if (t === "niche") return { bg: "#f3f4f6", fg: "#4b5563" }
  return null
}

export function getTierLabel(tier: string | null): string {
  if (!tier) return ""
  const t = tier.toLowerCase().trim()
  if (t === "mainstream") return "Popular"
  return t.charAt(0).toUpperCase() + t.slice(1)
}

/** Strip the brand-name prefix from a collection title so the list reads
 *  "French Door" instead of "Whirlpool French Door". Falls back to the raw
 *  title if the brand name isn't a prefix. */
export function stripBrandPrefix(title: string, brandName: string): string {
  const norm = title.trim()
  const prefix = brandName.trim()
  if (norm.toLowerCase().startsWith(prefix.toLowerCase() + " ")) {
    return norm.slice(prefix.length + 1).trim()
  }
  return norm
}

/** Format a type slug into a Title Case label for filter pills + meta lines. */
export function formatTypeLabel(type: string): string {
  return type
    .split("-")
    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ")
}

/**
 * Canonical badge set for a brand-page product.
 *
 * Routes the server `BrandProduct` row through the ONE canonical engine
 * (`badgesForSignals` → `determineBadges`) so the brand catalog/carousel cards
 * render the SAME badges (price_drop, energy_star, top_rated, premium, …) with
 * the SAME styling as collection/home/search cards. Replaces the old hand-rolled
 * ≥10% price-drop threshold + price-drop-only orange pill (spec §4.1 retired the
 * threshold in favor of ANY drop).
 *
 * Prices on `BrandProduct` are already in CENTS, so they feed `badgesForSignals`
 * directly (no dollars→cents conversion). `created_at` / `lowest_price_ever_cents`
 * are not part of the brand fetch, so the `new` / historical-low signals stay off
 * here — exactly what the data supports, no synthetic values.
 */
export function brandProductBadges(product: BrandProduct): BadgeData[] {
  const specs = (product.specs_json ?? {}) as {
    rating?: number
    reviews?: number
    energy_star?: boolean
  }
  return badgesForSignals({
    priceCents: product.lowest_price_cents ?? undefined,
    avgPrice30dCents: product.average_price_30d_cents ?? undefined,
    rating: typeof specs.rating === "number" ? specs.rating : undefined,
    reviews: typeof specs.reviews === "number" ? specs.reviews : undefined,
    energyStar: specs.energy_star === true,
  })
}
